import { Suspense, lazy, useEffect, useRef, useState } from "react";

const LocationMap = lazy(() => import("@/components/LocationMap"));

/**
 * Mounts the map only once its container approaches the viewport.
 *
 * LocationMap was imported statically by ServiceAreas, which the homepage
 * renders — so every visit to "/" eagerly pulled the Leaflet chunk, Leaflet's
 * stylesheet, and 37 OpenStreetMap tiles totalling 756 KiB, for a map that sits
 * several screens below the fold. Lighthouse also flagged the Leaflet CSS as
 * render-blocking: it is appended at media="print" and promoted on load, but
 * this page's first paint is late enough that the promotion landed before FCP.
 *
 * Deferring the mount moves all of it off the initial load. On /locations the
 * map is at the top of the page, so it intersects immediately and behaves as
 * before.
 *
 * The wrapper carries the same height as the map itself, so the space is
 * reserved up front and nothing shifts when the map arrives.
 */
const SIZING = "relative w-full h-[520px] md:h-[600px] lg:h-[680px] overflow-hidden";

const LazyLocationMap = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Start loading a little before it scrolls in, so tiles are there on arrival.
    const MARGIN = 300;

    // Deterministic fast path: if the map is already on screen at mount — which
    // is the case on /locations, where it sits at the top of the page — mount it
    // straight away instead of waiting for an observer callback. Every image on
    // the page declares width/height, so layout is settled by this point and
    // this measurement is trustworthy. It also means the map does not depend on
    // IntersectionObserver firing at all in the one case where it is the
    // page's primary content.
    const rect = el.getBoundingClientRect();
    const alreadyNearViewport = rect.top < window.innerHeight + MARGIN && rect.bottom > -MARGIN;
    if (alreadyNearViewport || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: `${MARGIN}px` }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={SIZING} style={{ zIndex: 0 }}>
      {visible && (
        <Suspense fallback={null}>
          <LocationMap />
        </Suspense>
      )}
    </div>
  );
};

export default LazyLocationMap;
